import { useEventListener } from "@vueuse/core";
import { computed, nextTick, onScopeDispose, ref, watch } from "vue";
import { useForm } from "#ce/composables/useForm";
import { useHostElement } from "#ce/composables/useHostElement";
import { CUSTOM_VALIDITY_EVENT } from "#ce/framework/events";
import { MOUNT_READY_EVENT, MOUNT_READY_MARKER } from "#ce/framework/mount-ready";

/**
 * The omnisearch's committed selection, and the required-skill error the host shows for it. The
 * host owns the error (`host.setCustomValidity`, `host.reportValidity`), so the wizard's gate
 * drives the same validity API as any form control. The field mirrors the error onto its inner
 * input and points the validation bubble at it.
 *
 * @param {Object} options
 * @param {import('vue').Ref} options.inputEl - template ref to the activator field host exposing `inputElement`
 * @param {import('vue').Ref<boolean>} options.open - the dropdown's open state
 */
export function useOmnisearchSelection({ inputEl, open }) {
  // The last committed selection — required-skill validation (owned by the
  // wizard) reads `hasSelection`; editing the text clears it.
  const selected = ref(null);

  // Drive the quantum-select's selection highlight from the committed selection
  // so clearing the field (which sets `selected` to null) also clears the menu's
  // active row on reopen.
  const selectedValues = computed(() => (selected.value?.value != null ? [selected.value.value] : []));

  const host = useHostElement();
  const { setValidity, customError } = useForm();

  function mirrorCustomError(message) {
    const input = inputEl.value?.inputElement;

    input?.setCustomValidity(message);

    if (message && input) {
      setValidity({}, "", input);
    }
  }

  function handleCustomValidity(event) {
    mirrorCustomError(event.detail ?? "");
  }

  // Live changes mirror synchronously, so a `reportValidity()` right after `setCustomValidity()`
  // already finds the inner input as its bubble anchor.
  if (typeof host?.addEventListener === "function") {
    useEventListener(host, CUSTOM_VALIDITY_EVENT, handleCustomValidity);
  }

  function mirrorCarriedError() {
    if (customError.value) {
      mirrorCustomError(customError.value);
    }
  }

  // An error the host carried before the field mounted reaches the inner input once the field's
  // exposed API is callable.
  function mirrorWhenFieldReady(field) {
    if (!field) {
      return;
    }

    if (field.hasAttribute?.(MOUNT_READY_MARKER) ?? true) {
      mirrorCarriedError();
    } else {
      field.addEventListener(MOUNT_READY_EVENT, mirrorCarriedError, { once: true });
    }
  }

  watch(inputEl, mirrorWhenFieldReady, { flush: "post" });

  // The host outlives this field, and its validity keeps the last anchor; an input that has left
  // the host's tree cannot anchor a later error.
  function releaseBubbleAnchor() {
    setValidity({});
  }

  onScopeDispose(releaseBubbleAnchor);

  // Typing and picking a suggestion each drop the error.
  function clearCustomValidity() {
    if (customError.value) {
      host.setCustomValidity("");
    }
  }

  let suppressFocusOpen = false;

  // Consumed by the root's focusin handler: true exactly once after a
  // programmatic `focusInput`, so open-on-focus does not reopen a just-closed
  // menu.
  function consumeSuppressedFocusOpen() {
    if (suppressFocusOpen) {
      suppressFocusOpen = false;
      return true;
    }
    return false;
  }

  function hasSelection() {
    return selected.value != null;
  }

  function focusInput() {
    // Programmatic refocus must not trip open-on-focus into reopening the menu.
    // Clear on the next tick so an already-focused input (which fires no focusin
    // to consume the flag) can't leave it stuck and suppress a later genuine open.
    suppressFocusOpen = true;
    inputEl.value?.inputElement?.focus?.();
    nextTick(() => {
      suppressFocusOpen = false;
    });
  }

  function openSearch() {
    open.value = true;
    inputEl.value?.inputElement?.focus?.();
  }

  return {
    selected,
    selectedValues,
    consumeSuppressedFocusOpen,
    hasSelection,
    clearCustomValidity,
    focusInput,
    openSearch,
  };
}
